<script setup lang="ts">
import { computed } from 'vue';
import { PlanResponse } from '@/planList/domain/PlanResponse';
import { AllowedFontSize, AllowedFontSizesMap, formatHelpers } from '@hfc/ui-core';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { useComparePlansStore } from '@/shared/infrastructure/composition/useComparePlansStore';
import { useCarrierInfo } from '@/application/shared/infrastructure/composition/useCarrierInfo';
import { twMerge } from 'tailwind-merge';

interface emitInterface {
  (event: 'setDetailsModal', plan: PlanResponse): void,
  (event: 'selectPlan', plan: PlanResponse): void,
  (event: 'setPlanToCompare', plan: PlanResponse): void,
  (event: 'unSelectPlan', plan: PlanResponse): void,
}

const emits = defineEmits<emitInterface>();

const { comparePlansStore, comparePlansState } = useComparePlansStore();

const { formatPrice } = formatHelpers();
const { getCarrierLogoId } = useCarrierInfo();

const props = withDefaults(defineProps<{
  plan: PlanResponse;
  cols: number;
  compact?: boolean;
  applicationType: APPLICATION_TYPES;
  viewDetailsButton?: boolean
  viewComparisonCheck?: boolean;
  isSelected?: boolean;
  disableSelectButton?: boolean;
  priceTitle?: string;
  priceTitleSize?: AllowedFontSize
  priceAttribute?: string;
  viewDetailsText?: string;
  hidePriceTitle?: boolean;
}>(), {
  viewDetailsButton: false,
  compact: false,
  priceTitle: 'You Pay',
  priceAttribute: 'rate',
  viewDetailsText: 'VIEW DETAILS',
  priceTitleSize: AllowedFontSize.LG
});

const comparisonMode = computed(() => comparePlansState.value.plansToCompare.length > 0);
const isSelectedToCompare = computed(() => comparePlansState.value.plansToCompare.find((plan) => plan.reference_id === props.plan.reference_id) ? true : false);

const planPriceAttribute = computed(() => (props.plan as unknown as Record<string, unknown>)[props.priceAttribute] as number);

const maxWidth = computed(() => props.compact ? 'max-w-[330px]' : 'max-w-[380px]');
const classResponsive = computed(() => props.cols === 1 ? 'w-full' : 'lg:w-1/2 w-full');

const itemClass = computed(() => `${classResponsive.value} ${maxWidth.value} ${comparisonMode.value ? 'cursor-pointer' : ''}`);

const recommendedTagClass = twMerge(
  'absolute top-16 -right-0 mt-2',
  props.compact && '-mt-2'
);

const setPlanToCompare = (plan: PlanResponse): void => {
  if (!isSelectedToCompare.value) {
    comparePlansStore.addPlanToCompare(plan);
  } else {
    comparePlansStore.removePlanToCompare(plan.reference_id);
  }
};
</script>

<template>
  <div class="flex-none" :class="itemClass">
    <div class="flex flex-col mx-4 h-full rounded-md drop-shadow-md text-center transition duration-300 bg-slate-50 hover:bg-white hover:drop-shadow-lg">
      <div class="flex justify-center items-center">
        <carrier-logo class="h-24 px-7 py-3 flex items-center" custom-class="max-w-full" :carrier-id="getCarrierLogoId(plan)" :coverage-type="applicationType" no-link="true" />
      </div>
      <recommended-tag v-if="plan.recommended" :class="recommendedTagClass" :style="compact ? 'zoom: 0.95' : ''"></recommended-tag>
      <div class="flex flex-grow items-center justify-center text-wrap py-5 text-blue font-semibold text-lg overflow-hidden" :class="{'h-22 px-10': compact, 'h-28 px-16': !compact}">
        <div class="flex flex-col">
          <slot name="plan-name">
            <p class="lining-nums">{{ plan.name }}</p>
          </slot>
          <span class="text-xs font-normal" v-if="plan.featured_benefit">({{ plan.featured_benefit }})</span>
        </div>
      </div>
      <div class="flex flex-col object-cover bg-blue py-3 text-slate-200 hover:text-white">
        <span v-if="!hidePriceTitle">{{ priceTitle }}</span>
        <span class="font-semibold lining-nums" :class="AllowedFontSizesMap[priceTitleSize]">{{ formatPrice(planPriceAttribute) }}<span class="text-xs">/month</span></span>
        <slot name="under-price"></slot>
      </div>
      <div class="flex flex-col space-y-2 content-center px-5" :class="{'py-6': compact, 'py-12': !compact}">
        <a v-if="viewDetailsButton" href="#" @click="emits('setDetailsModal', plan)">{{ viewDetailsText }}</a>
        <slot></slot>
      </div>
      <div class="content-end mb-5">
        <button v-if="!isSelected" type="button" class="rounded-md border  px-6 py-2 text-white font-semibold  hover:font-bold hover:drop-shadow-md transition duration-300" @click="emits('selectPlan', plan)" :disabled="disableSelectButton" :class="{ 'bg-red-600 hover:bg-red': !disableSelectButton, 'bg-gray-400 hover:bg-gray-500 cursor-not-allowed': disableSelectButton }"> Select Plan</button>
        <button v-else type="button" class="rounded-md border bg-green-600 px-6 py-2 text-white font-semibold hover:bg-green hover:font-bold hover:drop-shadow-md transition duration-300" @click="emits('unSelectPlan', plan)">
          <hfc-icon class="mr-1 " icon="circle-check" />Selected
        </button>
      </div>
      <div v-if="viewComparisonCheck" class="flex justify-center items-center mb-4">
        <label>Compare</label>
        <input :id="`compare-checkbox-${plan.reference_id}`" type="checkbox" @change="setPlanToCompare(plan)" v-model="isSelectedToCompare" class="ml-2 w-5 h-5 cursor-pointer" :disabled="comparePlansState.disableComparison && !isSelectedToCompare" />
      </div>
    </div>
  </div>
</template>
